import {
  CustomMappingType,
  ICustomMappingSourceOption,
  IFailedCustomMappingRecord,
  IPracticeMigration,
  ISourceEntityTypes,
} from '@principle-theorem/practice-migrations/interfaces';
import { Brand } from '@principle-theorem/principle-core';
import { SterilisableItem } from '@principle-theorem/principle-core/interfaces';
import {
  AtLeast,
  Firestore,
  IBlobFilenamePair,
  INamedDocument,
  WithRef,
  XSLXImporterExporter,
  asyncForEach,
  nameSorter,
  snapshot,
  toNamedDocument,
} from '@principle-theorem/shared';
import { Column } from 'exceljs';
import Fuse from 'fuse.js';
import { compact, first, sortBy } from 'lodash';
import { BaseCustomMappingHandler } from '../base-custom-mapping-handler';
import { CustomMapping } from '../custom-mapping';
import { PracticeMigration } from '../practice-migrations';
import { BaseSourceEntity } from '../source/base-source-entity';
import { TranslationMapHandler } from '../translation-map';
import { BaseSourceToXSLX, XSLXToBasicRecord } from './basic-source-to-xslx';

export interface IBaseMigrationSterilisationEquipment {
  id: string | number;
  name: string;
}

export const STERILISATION_EQUIPMENT_RESOURCE_TYPE = 'sterilisationEquipment';

export const STERILISATION_EQUIPMENT_MAPPING = CustomMapping.init({
  metadata: {
    label: 'Sterilisation Equipment',
    description: `Use this to map sterilisation equipment from your source PMS to a pack or equipment you setup in Principle.`,
    type: STERILISATION_EQUIPMENT_RESOURCE_TYPE,
  },
  type: CustomMappingType.DocumentReference,
  labelOverrides: {
    sourceIdentifier: 'Id',
    sourceLabel: 'Sterilisation Equipment',
    destinationIdentifier: 'Principle Sterilisation Pack/Equipment',
  },
});

export const STERILISATION_EQUIPMENT_HEADERS: AtLeast<
  Column,
  'key' | 'header'
>[] = [
  {
    key: 'label',
    header: 'Sterilisation Equipment',
  },
  {
    key: 'mapTo',
    header: 'Map To',
  },
];

class SterilisationEquipmentXSLX extends BaseSourceToXSLX<
  SterilisableItem,
  IBaseMigrationSterilisationEquipment
> {
  headers = STERILISATION_EQUIPMENT_HEADERS;
}

class XLSXToSterilisationEquipment extends XSLXToBasicRecord {
  headers = STERILISATION_EQUIPMENT_HEADERS;
}

export abstract class SterilisationEquipmentMappingHandler<
  SterilisationEquipmentRecord extends ISourceEntityTypes,
  SourceEntity extends BaseSourceEntity<SterilisationEquipmentRecord>,
> extends BaseCustomMappingHandler<SterilisableItem> {
  customMapping = STERILISATION_EQUIPMENT_MAPPING;
  abstract sourceEntity: SourceEntity;
  abstract translateFn: (
    record: SterilisationEquipmentRecord['data']
  ) => IBaseMigrationSterilisationEquipment;

  async getSourceOptions(
    migration: WithRef<IPracticeMigration>
  ): Promise<ICustomMappingSourceOption[]> {
    const records = await this.sourceEntity
      .getAllRecords$(migration)
      .toPromise();
    const options = records
      .map((record) => record.data)
      .map((record) => {
        const equipment = this.translateFn(record);
        return {
          label: equipment.name,
          value: this.sourceEntity.getSourceRecordId(record).toString(),
        };
      });

    return sortBy(options, 'label');
  }

  async getDestinationOptions(
    migration: WithRef<IPracticeMigration>
  ): Promise<INamedDocument<SterilisableItem>[]> {
    const brand = { ref: migration.configuration.brand.ref };
    const packs = await Firestore.getDocs(Brand.sterilisationPacksCol(brand));
    const equipment = await Firestore.getDocs(
      Brand.sterilisationEquipmentCol(brand)
    );
    return sortBy(
      [
        ...packs.map((pack) => ({
          ...toNamedDocument<SterilisableItem>(pack),
          name: `Pack: ${pack.name}`,
        })),
        ...equipment.map((item) => ({
          ...toNamedDocument<SterilisableItem>(item),
          name: `Equipment: ${item.name}`,
        })),
      ],
      nameSorter()
    );
  }

  async getMappingBlob(
    migration: WithRef<IPracticeMigration>
  ): Promise<IBlobFilenamePair> {
    const { fileName, equipment, translator } =
      await this._getExporterData(migration);

    return new XSLXImporterExporter().getBlob(fileName, equipment, translator);
  }

  async downloadMapping(migration: WithRef<IPracticeMigration>): Promise<void> {
    const { fileName, equipment, translator } =
      await this._getExporterData(migration);

    return new XSLXImporterExporter().download(fileName, equipment, translator);
  }

  async uploadMapping(
    migration: WithRef<IPracticeMigration>,
    file: Blob
  ): Promise<void> {
    const items = await new XSLXImporterExporter().parse(
      file,
      new XLSXToSterilisationEquipment()
    );

    const translationMap = new TranslationMapHandler(migration);
    const sourceOptions = await this.getSourceOptions(migration);
    const destinationOptions = await this.getDestinationOptions(migration);

    const failedRecords: IFailedCustomMappingRecord[] = [];

    await asyncForEach(items, async (item) => {
      const mapTo = item.mapTo;
      const matchingOption = sourceOptions.find(
        (option) => option.label === item.label
      );
      if (!matchingOption || !mapTo) {
        return;
      }

      const foundEquipment = destinationOptions.find(
        (equipment) => equipment.name === mapTo
      );
      if (!foundEquipment) {
        // eslint-disable-next-line no-console
        console.error(
          `Mapping error: ${this.customMapping.metadata.label} - Couldn't find pack or equipment for item`,
          item
        );

        failedRecords.push({
          sourceIdentifier: matchingOption.value,
          sourceLabel: matchingOption.label,
          reason: `Destination identifier "${mapTo?.trim()}" not found`,
        });
        return;
      }

      await this.upsertRecord(
        {
          destinationIdentifier: foundEquipment.ref,
          sourceIdentifier: matchingOption.value,
          destinationValue: { value: item.mapTo },
          sourceLabel: matchingOption.label,
        },
        translationMap
      );
    });

    const customMapping = await snapshot(
      PracticeMigration.findCustomMapping$(migration, this.customMapping)
    );
    if (!customMapping) {
      throw new Error('Custom mapping not found for updating failed records');
    }
    await Firestore.patchDoc(customMapping.ref, {
      failedRecords,
    });
  }

  async autocompleteMappings(
    migration: WithRef<IPracticeMigration>
  ): Promise<void> {
    const translationMap = new TranslationMapHandler(migration);
    const sourceOptions = await this.getSourceOptions(migration);
    const destinationOptions = await this.getDestinationOptions(migration);
    const records = await this.getRecords(translationMap);

    const mappedItems = records
      .filter((record) => !!record.destinationIdentifier)
      .map((record) => record.sourceIdentifier);

    const unmappedOptions = sourceOptions.filter(
      (option) => !mappedItems.includes(option.value)
    );

    const uploadRecords = unmappedOptions.map((option) => {
      const fuse = new Fuse(destinationOptions, {
        keys: ['name'],
        includeScore: true,
        threshold: 0.3,
      });
      const matches = fuse.search(option.label);
      const match = first(matches)?.item;

      return match
        ? {
            destinationIdentifier: match.ref,
            destinationValue: { value: match.name },
            sourceIdentifier: option.value,
            sourceLabel: option.label,
          }
        : undefined;
    });

    await this.upsertBulk(compact(uploadRecords), translationMap);
  }

  private async _getExporterData(
    migration: WithRef<IPracticeMigration>
  ): Promise<{
    fileName: string;
    equipment: IBaseMigrationSterilisationEquipment[];
    translator: SterilisationEquipmentXSLX;
  }> {
    const fileName = this.getFileName();
    const equipment = await this._getSourceEquipment(migration);
    const translationMap = new TranslationMapHandler(migration);
    const translator = new SterilisationEquipmentXSLX(
      await this.getDestinationOptions(migration),
      await this.getRecords(translationMap)
    );
    return { fileName, equipment, translator };
  }

  private async _getSourceEquipment(
    migration: WithRef<IPracticeMigration>
  ): Promise<IBaseMigrationSterilisationEquipment[]> {
    const records = await this.sourceEntity
      .getAllRecords$(migration)
      .toPromise();
    return sortBy(
      records.map((record) => this.translateFn(record.data)),
      'name'
    );
  }
}
